import { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Plus, Edit, Trash2, Search } from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useCreateTechnology, useUpdateTechnology } from '../hooks'
import { useManufacturers, useDeleteManufacturer } from '@/features/manufacturers/hooks'
import { useCategories, useDeleteCategory } from '@/features/categories/hooks'
import { ManufacturerDialog } from '@/features/manufacturers/components/ManufacturerDialog'
import { CategoryDialog } from '@/features/categories/components/CategoryDialog'
import { useCan } from '@/hooks/useCan'
import { GenericDeleteDialog } from '@/components/dialogs/GenericDeleteDialog'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'
import type { Technology, Manufacturer, TechnologyCategory } from '@/types/entities'

const technologySchema = z.object({
    manufacturerId: z.number().min(1, 'Manufacturer is required'),
    categoryId: z.number().min(1, 'Category is required'),
    model: z.string().min(1, 'Model is required'),
    description: z.string().optional().nullable(),
    isSaaS: z.boolean().optional(),
})

type TechnologyFormData = z.infer<typeof technologySchema>

interface TechnologyFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    technology: Technology | null
}

export function TechnologyFormDialog({ open, onOpenChange, technology }: TechnologyFormDialogProps) {
    const { t } = useTranslation()
    const isEditing = !!technology
    const createTechnology = useCreateTechnology()
    const updateTechnology = useUpdateTechnology()
    
    const { data: manufacturers = [], isLoading: isLoadingManufacturers } = useManufacturers()
    const { data: categories = [], isLoading: isLoadingCategories } = useCategories()
    const deleteManufacturer = useDeleteManufacturer()
    const deleteCategory = useDeleteCategory()
    const { toast } = useToast()
    // Manufacturers and categories are reference data of their own, each with its own permissions:
    // editing a technology does not bring the right to add, rename or delete the lists it picks from.
    const canCreateManufacturer = useCan('manufacturers.create')
    const canUpdateManufacturer = useCan('manufacturers.update')
    const canDeleteManufacturer = useCan('manufacturers.delete')
    const canCreateCategory = useCan('categories.create')
    const canUpdateCategory = useCan('categories.update')
    const canDeleteCategory = useCan('categories.delete')
    
    const [manufacturerDialogOpen, setManufacturerDialogOpen] = useState(false)
    const [selectedManufacturer, setSelectedManufacturer] = useState<Manufacturer | null>(null)
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
    const [selectedCategory, setSelectedCategory] = useState<TechnologyCategory | null>(null)
    const [deleteManufacturerDialogOpen, setDeleteManufacturerDialogOpen] = useState(false)
    const [manufacturerToDelete, setManufacturerToDelete] = useState<Manufacturer | null>(null)
    const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false)
    const [categoryToDelete, setCategoryToDelete] = useState<TechnologyCategory | null>(null)
    const [manufacturerSearch, setManufacturerSearch] = useState('')
    const [categorySearch, setCategorySearch] = useState('')

    const form = useForm<TechnologyFormData>({
        resolver: zodResolver(technologySchema),
        mode: 'onChange',
        defaultValues: {
            manufacturerId: 0,
            categoryId: 0,
            model: '',
            description: '',
            isSaaS: false,
        },
    })

    // Track if we've initialized the form to prevent resetting on manufacturers/categories refetch
    const formInitializedRef = useRef(false)
    
    useEffect(() => {
        if (open && !formInitializedRef.current) {
            if (technology) {
                // Find manufacturer and category IDs from the technology data
                const manufacturerId = technology.manufacturerId || 
                    manufacturers.find(m => m.name === technology.manufacturer)?.id || 0
                const categoryId = technology.categoryId || 
                    categories.find(c => c.name === technology.category)?.id || 0
                
                form.reset({
                    manufacturerId: manufacturerId || 0,
                    categoryId: categoryId || 0,
                    model: technology.model || '',
                    description: technology.description || '',
                    isSaaS: technology.isSaaS === true,
                })
            } else {
                form.reset({
                    manufacturerId: 0,
                    categoryId: 0,
                    model: '',
                    description: '',
                    isSaaS: false,
                })
            }
            formInitializedRef.current = true
        } else if (!open) {
            // Reset the flag when dialog closes
            formInitializedRef.current = false
            // Clear search when dialog closes
            setManufacturerSearch('')
            setCategorySearch('')
        }
    }, [open, technology, form, manufacturers, categories])

    // Watch form values to ensure buttons stay visible (after form is initialized)
    // Convert 0 to null to prevent React from rendering "0" as text
    const manufacturerId = form.watch('manufacturerId') || null
    const categoryId = form.watch('categoryId') || null

    const onSubmit = async (data: TechnologyFormData) => {
        try {
            // Prepare data for submission - convert empty strings to null for optional fields
            const submitData = {
                manufacturerId: data.manufacturerId,
                categoryId: data.categoryId,
                model: data.model,
                description: data.description?.trim() || null,
                isSaaS: data.isSaaS === true,
            }
            
            if (isEditing && technology) {
                await updateTechnology.mutateAsync({
                    id: technology.id,
                    data: submitData,
                })
            } else {
                await createTechnology.mutateAsync(submitData)
            }
            onOpenChange(false)
        } catch (error) {
            // Error handling is done in the hooks
        }
    }
    
    const handleCreateManufacturer = () => {
        setSelectedManufacturer(null)
        setManufacturerDialogOpen(true)
    }
    
    const handleEditManufacturer = (manufacturerId: number) => {
        const manufacturer = manufacturers.find(m => m.id === manufacturerId)
        setSelectedManufacturer(manufacturer || null)
        setManufacturerDialogOpen(true)
    }
    
    const handleDeleteManufacturer = (idToDelete: number) => {
        const manufacturer = manufacturers.find(m => m.id === idToDelete)
        if (manufacturer) {
            setManufacturerToDelete(manufacturer)
            setDeleteManufacturerDialogOpen(true)
        }
    }
    
    const confirmDeleteManufacturer = async () => {
        if (!manufacturerToDelete) return
        
        try {
            await deleteManufacturer.mutateAsync(manufacturerToDelete.id)
            // Clear the selection if the deleted manufacturer was selected
            if (manufacturerId === manufacturerToDelete.id) {
                form.setValue('manufacturerId', 0, { shouldValidate: true })
            }
            setDeleteManufacturerDialogOpen(false)
            setManufacturerToDelete(null)
        } catch (error: any) {
            const errorMessage = handleApiError(error)
            toast({
                title: 'Cannot delete manufacturer',
                description: errorMessage,
                variant: 'destructive',
            })
        }
    }
    
    const handleCreateCategory = () => {
        setSelectedCategory(null)
        setCategoryDialogOpen(true)
    }
    
    const handleEditCategory = (categoryId: number) => {
        const category = categories.find(c => c.id === categoryId)
        setSelectedCategory(category || null)
        setCategoryDialogOpen(true)
    }
    
    const handleDeleteCategory = (idToDelete: number) => {
        const category = categories.find(c => c.id === idToDelete)
        if (category) {
            setCategoryToDelete(category)
            setDeleteCategoryDialogOpen(true)
        }
    }
    
    const confirmDeleteCategory = async () => {
        if (!categoryToDelete) return
        
        try {
            await deleteCategory.mutateAsync(categoryToDelete.id)
            // Clear the selection if the deleted category was selected
            if (categoryId === categoryToDelete.id) {
                form.setValue('categoryId', 0, { shouldValidate: true })
            }
            setDeleteCategoryDialogOpen(false)
            setCategoryToDelete(null)
        } catch (error: any) {
            const errorMessage = handleApiError(error)
            toast({
                title: 'Cannot delete category',
                description: errorMessage,
                variant: 'destructive',
            })
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>
                        {isEditing ? t('technologies.editTechnology') : t('technologies.createTechnology')}
                    </DialogTitle>
                    <DialogDescription>
                        {isEditing
                            ? t('technologies.editTechnologyDescription')
                            : t('technologies.createTechnologyDescription')}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="manufacturer">{t('technologies.manufacturer')} <span className="text-destructive">*</span></Label>
                        <div className="flex gap-2">
                            {manufacturerId ? (
                                <Select
                                    value={manufacturerId.toString()}
                                    onValueChange={(value) => {
                                        if (value) {
                                            form.setValue('manufacturerId', parseInt(value), { shouldValidate: true })
                                        } else {
                                            form.setValue('manufacturerId', 0, { shouldValidate: true })
                                        }
                                    }}
                                >
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder={t('technologies.manufacturerPlaceholder')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <div className="px-2 py-1.5 border-b">
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                <Input
                                                    placeholder="Search manufacturers..."
                                                    value={manufacturerSearch}
                                                    onChange={(e) => setManufacturerSearch(e.target.value)}
                                                    className="pl-8 h-8"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                        {isLoadingManufacturers ? (
                                            <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                                        ) : manufacturers
                                            .filter((manufacturer) => {
                                                if (!manufacturerSearch) return true
                                                const searchLower = manufacturerSearch.toLowerCase()
                                                const name = (manufacturer.name || '').toLowerCase()
                                                return name.includes(searchLower)
                                            })
                                            .length > 0 ? (
                                            manufacturers
                                                .filter((manufacturer) => {
                                                    if (!manufacturerSearch) return true
                                                    const searchLower = manufacturerSearch.toLowerCase()
                                                    const name = (manufacturer.name || '').toLowerCase()
                                                    return name.includes(searchLower)
                                                })
                                                .map((manufacturer) => (
                                                    <SelectItem key={manufacturer.id} value={manufacturer.id.toString()}>
                                                        {manufacturer.name}
                                                    </SelectItem>
                                                ))
                                        ) : (
                                            <SelectItem value="__empty__" disabled>
                                                {manufacturerSearch ? 'No manufacturers found' : 'No manufacturers available'}
                                            </SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <Select
                                    onValueChange={(value) => {
                                        if (value) {
                                            form.setValue('manufacturerId', parseInt(value), { shouldValidate: true })
                                        }
                                    }}
                                >
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder={t('technologies.manufacturerPlaceholder')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <div className="px-2 py-1.5 border-b">
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                <Input
                                                    placeholder="Search manufacturers..."
                                                    value={manufacturerSearch}
                                                    onChange={(e) => setManufacturerSearch(e.target.value)}
                                                    className="pl-8 h-8"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                        {isLoadingManufacturers ? (
                                            <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                                        ) : manufacturers
                                            .filter((manufacturer) => {
                                                if (!manufacturerSearch) return true
                                                const searchLower = manufacturerSearch.toLowerCase()
                                                const name = (manufacturer.name || '').toLowerCase()
                                                return name.includes(searchLower)
                                            })
                                            .length > 0 ? (
                                            manufacturers
                                                .filter((manufacturer) => {
                                                    if (!manufacturerSearch) return true
                                                    const searchLower = manufacturerSearch.toLowerCase()
                                                    const name = (manufacturer.name || '').toLowerCase()
                                                    return name.includes(searchLower)
                                                })
                                                .map((manufacturer) => (
                                                    <SelectItem key={manufacturer.id} value={manufacturer.id.toString()}>
                                                        {manufacturer.name}
                                                    </SelectItem>
                                                ))
                                        ) : (
                                            <SelectItem value="__empty__" disabled>
                                                {manufacturerSearch ? 'No manufacturers found' : 'No manufacturers available'}
                                            </SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            )}
                            {manufacturerId && (
                                <>
                                    {canUpdateManufacturer && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleEditManufacturer(manufacturerId)}
                                            title="Edit manufacturer"
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                    )}
                                    {canDeleteManufacturer && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDeleteManufacturer(manufacturerId)}
                                            title="Delete manufacturer"
                                            className="text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </>
                            )}
                            {canCreateManufacturer && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button type="button" variant="ghost" size="icon" title="Add manufacturer">
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={handleCreateManufacturer}>
                                            <Plus className="mr-2 h-4 w-4" />
                                            Create
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                        {form.formState.errors.manufacturerId && (
                            <p className="text-sm text-destructive">
                                {form.formState.errors.manufacturerId.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="category">{t('technologies.category')} <span className="text-destructive">*</span></Label>
                        <div className="flex gap-2">
                            {categoryId ? (
                                <Select
                                    value={categoryId.toString()}
                                    onValueChange={(value) => {
                                        if (value) {
                                            form.setValue('categoryId', parseInt(value), { shouldValidate: true })
                                        } else {
                                            form.setValue('categoryId', 0, { shouldValidate: true })
                                        }
                                    }}
                                >
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder={t('technologies.categoryPlaceholder')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <div className="px-2 py-1.5 border-b">
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                <Input
                                                    placeholder="Search categories..."
                                                    value={categorySearch}
                                                    onChange={(e) => setCategorySearch(e.target.value)}
                                                    className="pl-8 h-8"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                        {isLoadingCategories ? (
                                            <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                                        ) : categories
                                            .filter((category) => {
                                                if (!categorySearch) return true
                                                const searchLower = categorySearch.toLowerCase()
                                                const name = (category.name || '').toLowerCase()
                                                return name.includes(searchLower)
                                            })
                                            .length > 0 ? (
                                            categories
                                                .filter((category) => {
                                                    if (!categorySearch) return true
                                                    const searchLower = categorySearch.toLowerCase()
                                                    const name = (category.name || '').toLowerCase()
                                                    return name.includes(searchLower)
                                                })
                                                .map((category) => (
                                                    <SelectItem key={category.id} value={category.id.toString()}>
                                                        {category.name}
                                                    </SelectItem>
                                                ))
                                        ) : (
                                            <SelectItem value="__empty__" disabled>
                                                {categorySearch ? 'No categories found' : 'No categories available'}
                                            </SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <Select
                                    onValueChange={(value) => {
                                        if (value) {
                                            form.setValue('categoryId', parseInt(value), { shouldValidate: true })
                                        }
                                    }}
                                >
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder={t('technologies.categoryPlaceholder')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <div className="px-2 py-1.5 border-b">
                                            <div className="relative">
                                                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                                <Input
                                                    placeholder="Search categories..."
                                                    value={categorySearch}
                                                    onChange={(e) => setCategorySearch(e.target.value)}
                                                    className="pl-8 h-8"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onKeyDown={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </div>
                                        {isLoadingCategories ? (
                                            <SelectItem value="__loading__" disabled>Loading...</SelectItem>
                                        ) : categories
                                            .filter((category) => {
                                                if (!categorySearch) return true
                                                const searchLower = categorySearch.toLowerCase()
                                                const name = (category.name || '').toLowerCase()
                                                return name.includes(searchLower)
                                            })
                                            .length > 0 ? (
                                            categories
                                                .filter((category) => {
                                                    if (!categorySearch) return true
                                                    const searchLower = categorySearch.toLowerCase()
                                                    const name = (category.name || '').toLowerCase()
                                                    return name.includes(searchLower)
                                                })
                                                .map((category) => (
                                                    <SelectItem key={category.id} value={category.id.toString()}>
                                                        {category.name}
                                                    </SelectItem>
                                                ))
                                        ) : (
                                            <SelectItem value="__empty__" disabled>
                                                {categorySearch ? 'No categories found' : 'No categories available'}
                                            </SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            )}
                            {categoryId && (
                                <>
                                    {canUpdateCategory && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleEditCategory(categoryId)}
                                            title="Edit category"
                                        >
                                            <Edit className="h-4 w-4" />
                                        </Button>
                                    )}
                                    {canDeleteCategory && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDeleteCategory(categoryId)}
                                            title="Delete category"
                                            className="text-destructive hover:text-destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </>
                            )}
                            {canCreateCategory && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button type="button" variant="ghost" size="icon" title="Add category">
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={handleCreateCategory}>
                                            <Plus className="mr-2 h-4 w-4" />
                                            Create
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                        {form.formState.errors.categoryId && (
                            <p className="text-sm text-destructive">
                                {form.formState.errors.categoryId.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="model">{t('technologies.model')} <span className="text-destructive">*</span></Label>
                        <Input
                            id="model"
                            {...form.register('model')}
                            placeholder={t('technologies.modelPlaceholder')}
                        />
                        {form.formState.errors.model && (
                            <p className="text-sm text-destructive">
                                {form.formState.errors.model.message}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">{t('technologies.description')}</Label>
                        <Textarea
                            id="description"
                            {...form.register('description')}
                            placeholder={t('technologies.descriptionPlaceholder')}
                            rows={3}
                        />
                    </div>

                    {/* SaaS is said here, on the technology, and an object on it is a SaaS application. */}
                    <label className="flex items-start gap-2 text-sm">
                        <input type="checkbox" className="mt-1 h-4 w-4" checked={form.watch('isSaaS') === true} onChange={(e) => form.setValue('isSaaS', e.target.checked, { shouldDirty: true })} />
                        <span>
                            <span className="font-medium">{t('technologies.isSaaS', 'SaaS: run by its vendor')}</span>
                            <span className="block text-xs text-muted-foreground">{t('technologies.isSaaSHelp', 'The vendor recovers it, not the customer. A backup of its data may still give a tactical solution until it is back.')}</span>
                        </span>
                    </label>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={createTechnology.isPending || updateTechnology.isPending}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={createTechnology.isPending || updateTechnology.isPending}
                        >
                            {createTechnology.isPending || updateTechnology.isPending
                                ? t('common.saving')
                                : isEditing
                                ? t('common.save')
                                : t('technologies.createTechnology')}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
            
            <ManufacturerDialog
                open={manufacturerDialogOpen}
                onOpenChange={setManufacturerDialogOpen}
                manufacturer={selectedManufacturer}
            />
            
            <CategoryDialog
                open={categoryDialogOpen}
                onOpenChange={setCategoryDialogOpen}
                category={selectedCategory}
            />
            
            <GenericDeleteDialog
                open={deleteManufacturerDialogOpen}
                onOpenChange={setDeleteManufacturerDialogOpen}
                item={manufacturerToDelete}
                onConfirm={confirmDeleteManufacturer}
                isLoading={deleteManufacturer.isPending}
            />
            
            <GenericDeleteDialog
                open={deleteCategoryDialogOpen}
                onOpenChange={setDeleteCategoryDialogOpen}
                item={categoryToDelete}
                onConfirm={confirmDeleteCategory}
                isLoading={deleteCategory.isPending}
            />
        </Dialog>
    )
}
